// src/pages/talleres/editar/FechasForm.jsx
import React from 'react';
import { Form, DatePicker, Card, Alert, Space, Tag, Divider } from 'antd';
import { CalendarOutlined, InfoCircleOutlined, ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

const { RangePicker } = DatePicker;

export default function FechasForm({ form, disabled = false }) {
  // 🆕 Watch de campos separados con hora
  const watchInscripcionInicio = Form.useWatch('inscripcionInicio', form);
  const watchInscripcionFin = Form.useWatch('inscripcionFin', form);
  const watchFechasActividades = Form.useWatch('fechasActividades', form);
  
  // Obtener tipoDuracionSuscripcion desde formInfo
  const formInfo = Form.useFormInstance() || form;
  const watchTipoDuracion = Form.useWatch('tipoDuracionSuscripcion', formInfo);

  // 🆕 Validar fechas de inscripción (ahora con hora)
  const tieneFechasInscripcion = watchInscripcionInicio && watchInscripcionFin;

  // Validar fechas de actividades
  const tieneFechasActividades = watchFechasActividades && 
                                  Array.isArray(watchFechasActividades) && 
                                  watchFechasActividades.length === 2 &&
                                  watchFechasActividades[0] && 
                                  watchFechasActividades[1];

  // Calcular duración del taller en días
  const diasTaller = tieneFechasActividades 
    ? dayjs(watchFechasActividades[1]).diff(dayjs(watchFechasActividades[0]), 'day')
    : null;

  // Detectar si es taller corto
  const esTallerCorto = diasTaller !== null && diasTaller < 30;

  // 🆕 Validar coherencia entre fechas de inscripción y actividades
  const fechasIncoherentes = tieneFechasInscripcion && tieneFechasActividades &&
    dayjs(watchInscripcionFin).isAfter(dayjs(watchFechasActividades[0]));

  // 🆕 Verificar si la apertura es hoy
  const aperturaEsHoy = watchInscripcionInicio && 
    dayjs(watchInscripcionInicio).isSame(dayjs(), 'day');

  // 🆕 Verificar si ya pasó la hora de apertura
  const yaAbrio = watchInscripcionInicio && 
    dayjs(watchInscripcionInicio).isBefore(dayjs());

  // 🆕 Calcular tiempo hasta apertura
  const tiempoHastaApertura = watchInscripcionInicio && !yaAbrio
    ? {
        dias: dayjs(watchInscripcionInicio).diff(dayjs(), 'day'),
        horas: dayjs(watchInscripcionInicio).diff(dayjs(), 'hour') % 24,
        minutos: dayjs(watchInscripcionInicio).diff(dayjs(), 'minute') % 60
      }
    : null;

  // Obtener descripción de la modalidad
  const getDescripcionModalidad = () => {
    switch (watchTipoDuracion) {
      case 'mes_calendario':
        return 'Mismo día cada mes (ej: 15/01 → 15/02)';
      case 'fin_de_mes':
        return 'Hasta fin de mes (ej: 15/01 → 31/01)';
      case 'mes_corrido':
        return 'Siempre 30 días exactos';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4">
      <Alert
        message="Gestión de fechas del taller"
        description="Define los períodos de inscripción y las fechas de las actividades. Ambos son opcionales pero te dan mayor control."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
      />

      {/* Fechas de actividades */}
      <Card title="📅 Periodo de actividades del taller">
        <Alert
          message="¿Qué son las fechas de actividades?"
          description={
            <div className="space-y-2">
              <p>Este periodo define <strong>cuándo comienza y termina realmente el taller</strong>:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Con fechas definidas:</strong> Las suscripciones inician el día de inicio de actividades, no el día de compra</li>
                <li><strong>Inscripción anticipada:</strong> Los usuarios pueden pagar con anticipación, pero la suscripción espera hasta el inicio</li>
                <li><strong>Talleres cortos (&lt;30 días):</strong> Se detectan automáticamente y la suscripción dura exactamente ese período</li>
                <li><strong>Sin fechas:</strong> El taller funciona como siempre (suscripción inicia el día de compra)</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          className="mb-4"
        />

        <Form.Item
          label="Rango de fechas de actividades"
          name="fechasActividades"
          tooltip="Define cuándo comienza y termina el taller realmente"
          rules={[
            {
              validator: async (_, value) => {
                if (value && value[0] && value[1]) {
                  const inicio = dayjs(value[0]);
                  const fin = dayjs(value[1]);
                  
                  if (fin.isBefore(inicio) || fin.isSame(inicio, 'day')) {
                    throw new Error('La fecha de fin debe ser posterior a la fecha de inicio');
                  }
                }
              }
            }
          ]}
        >
          <RangePicker
            size="large"
            format="DD/MM/YYYY"
            placeholder={['Primer día de clases', 'Último día de clases']}
            className="w-full"
            disabled={disabled}
          />
        </Form.Item>

        {tieneFechasActividades && (
          <>
            {esTallerCorto && (
              <Alert
                message="⚡ Taller corto detectado"
                description={
                  <div className="space-y-2">
                    <p><strong>Duración: {diasTaller} días</strong></p>
                    <p>Como el taller dura menos de 30 días, la suscripción será exactamente desde el inicio hasta el fin de actividades, independiente del plan que compren los usuarios.</p>
                  </div>
                }
                type="warning"
                showIcon
                className="mb-4"
              />
            )}

            <Alert
              message="✅ Periodo de actividades configurado"
              description={
                <Space direction="vertical" size="small" className="w-full">
                  <div>
                    <CalendarOutlined /> <strong>Inicia:</strong> {dayjs(watchFechasActividades[0]).format('DD/MM/YYYY')}
                  </div>
                  <div>
                    <CalendarOutlined /> <strong>Termina:</strong> {dayjs(watchFechasActividades[1]).format('DD/MM/YYYY')}
                  </div>
                  <div className="mt-2">
                    <Tag color={esTallerCorto ? "orange" : "blue"}>
                      {diasTaller} días de duración
                    </Tag>
                    {!esTallerCorto && watchTipoDuracion && (
                      <Tag color="blue">
                        Suscripciones según modalidad: {getDescripcionModalidad()}
                      </Tag>
                    )}
                  </div>
                  <Divider style={{ margin: '12px 0' }} />
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="font-semibold mb-1">
                      <ClockCircleOutlined /> ¿Cuándo inicia la suscripción?
                    </p>
                    <p>
                      {esTallerCorto 
                        ? `Las suscripciones durarán exactamente ${diasTaller} días (desde ${dayjs(watchFechasActividades[0]).format('DD/MM/YYYY')} hasta ${dayjs(watchFechasActividades[1]).format('DD/MM/YYYY')})`
                        : `Las suscripciones iniciarán el ${dayjs(watchFechasActividades[0]).format('DD/MM/YYYY')} aunque el usuario compre antes`
                      }
                    </p>
                  </div>
                </Space>
              }
              type="success"
              showIcon
              className="mt-4"
            />
          </>
        )}

        {!tieneFechasActividades && (
          <Alert
            message="Sin periodo definido"
            description="Las suscripciones iniciarán el día de la compra de cada usuario (comportamiento estándar para talleres permanentes)."
            type="info"
            showIcon
            className="mt-4"
          />
        )}
      </Card>

      {/* 🆕 Fechas de inscripción CON HORA INTEGRADA */}
      <Card title="📝 Periodo de inscripción">
        <Alert
          message="¿Qué son las fechas de inscripción?"
          description={
            <div className="space-y-2">
              <p>Este periodo define <strong>cuándo</strong> los usuarios pueden registrarse al taller:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Antes de la fecha/hora de inicio:</strong> Los usuarios no podrán inscribirse (aún no abierto)</li>
                <li><strong>Durante el periodo:</strong> Las inscripciones están habilitadas</li>
                <li><strong>Después de la fecha de fin:</strong> Las inscripciones se cierran automáticamente</li>
                <li><strong>Sin fechas:</strong> Las inscripciones se controlan manualmente desde el panel de administración</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          className="mb-4"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 🆕 Fecha y hora de INICIO de inscripciones */}
          <Form.Item
            label={
              <Space>
                <ClockCircleOutlined />
                <span>Apertura de inscripciones</span>
              </Space>
            }
            name="inscripcionInicio"
            tooltip="Define la fecha y hora exacta en que se abrirán las inscripciones"
            rules={[
              {
                validator: async (_, value) => {
                  if (value) {
                    // Validar coherencia con fin de inscripción
                    if (watchInscripcionFin && dayjs(value).isAfter(dayjs(watchInscripcionFin))) {
                      throw new Error('La apertura debe ser antes del cierre');
                    }

                    // Validar coherencia con inicio de actividades
                    if (tieneFechasActividades && dayjs(value).isAfter(dayjs(watchFechasActividades[0]))) {
                      throw new Error('La apertura debe ser antes del inicio de actividades');
                    }
                  }
                }
              }
            ]}
          >
            <DatePicker
              showTime={{
                format: 'HH:mm',
                minuteStep: 15,
                showNow: false,
              }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Selecciona fecha y hora"
              className="w-full"
              size="large"
              disabled={disabled}
            />
          </Form.Item>

          {/* 🆕 Fecha y hora de FIN de inscripciones */}
          <Form.Item
            label={
              <Space>
                <ClockCircleOutlined />
                <span>Cierre de inscripciones</span>
              </Space>
            }
            name="inscripcionFin"
            tooltip="Define la fecha y hora exacta en que se cerrarán las inscripciones"
            rules={[
              {
                validator: async (_, value) => {
                  if (value) {
                    // Validar que sea después del inicio
                    if (watchInscripcionInicio && dayjs(value).isBefore(dayjs(watchInscripcionInicio))) {
                      throw new Error('El cierre debe ser después de la apertura');
                    }

                    // Validar coherencia con inicio de actividades
                    if (tieneFechasActividades && dayjs(value).isAfter(dayjs(watchFechasActividades[0]))) {
                      throw new Error('El cierre debe ser antes o el mismo día del inicio de actividades');
                    }
                  }
                }
              }
            ]}
          >
            <DatePicker
              showTime={{
                format: 'HH:mm',
                minuteStep: 15,
                showNow: false,
                defaultValue: dayjs().hour(23).minute(59),
              }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Selecciona fecha y hora"
              className="w-full"
              size="large"
              disabled={disabled}
              disabledDate={(current) => {
                if (!current) return false;
                if (watchInscripcionInicio) {
                  return current < dayjs(watchInscripcionInicio).startOf('day');
                }
                return false;
              }}
            />
          </Form.Item>
        </div>

        {fechasIncoherentes && (
          <Alert
            message="⚠️ Fechas incoherentes"
            description="Las inscripciones deben cerrar antes o el mismo día que comienzan las actividades. Ajusta las fechas."
            type="error"
            showIcon
            icon={<WarningOutlined />}
            className="mb-4"
          />
        )}

        {/* 🆕 Vista previa de la configuración */}
        {tieneFechasInscripcion && (
          <Alert
            message={yaAbrio ? "⚠️ Las inscripciones ya están abiertas con esta configuración" : "✅ Ventana de inscripción configurada"}
            description={
              <Space direction="vertical" size="small" className="w-full">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="font-semibold mb-2">
                    <ClockCircleOutlined /> Cronología de inscripciones:
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">🟢</span>
                      <div>
                        <strong>Apertura:</strong><br />
                        {dayjs(watchInscripcionInicio).format('dddd, DD [de] MMMM [de] YYYY [a las] HH:mm')} hrs
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">🔴</span>
                      <div>
                        <strong>Cierre:</strong><br />
                        {dayjs(watchInscripcionFin).format('dddd, DD [de] MMMM [de] YYYY [a las] HH:mm')} hrs
                      </div>
                    </div>

                    <Divider style={{ margin: '12px 0' }} />

                    <div>
                      <Tag color={yaAbrio ? "red" : "blue"} className="mb-2">
                        Ventana de {dayjs(watchInscripcionFin).diff(dayjs(watchInscripcionInicio), 'day')} días, {dayjs(watchInscripcionFin).diff(dayjs(watchInscripcionInicio), 'hour') % 24} horas
                      </Tag>

                      {aperturaEsHoy && !yaAbrio && tiempoHastaApertura && (
                        <Tag color="orange" icon={<ClockCircleOutlined />}>
                          Abre HOY en {tiempoHastaApertura.horas}h {tiempoHastaApertura.minutos}min
                        </Tag>
                      )}

                      {!aperturaEsHoy && !yaAbrio && tiempoHastaApertura && (
                        <Tag color="cyan" icon={<ClockCircleOutlined />}>
                          Abre en {tiempoHastaApertura.dias} días, {tiempoHastaApertura.horas}h
                        </Tag>
                      )}

                      {yaAbrio && (
                        <Tag color="red" icon={<WarningOutlined />}>
                          Esta hora ya pasó - las inscripciones abrirán inmediatamente
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>

                {tieneFechasActividades && (
                  <div className="mt-2 bg-blue-50 p-3 rounded">
                    <p className="text-sm">
                      <strong>📆 Período de espera:</strong> {dayjs(watchFechasActividades[0]).diff(dayjs(watchInscripcionFin), 'day')} días entre cierre de inscripciones e inicio de actividades
                    </p>
                  </div>
                )}

                {/* Información de zona horaria */}
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                  <InfoCircleOutlined /> Todas las horas están en zona horaria de Chile (UTC-3)
                </div>
              </Space>
            }
            type={yaAbrio ? "warning" : "success"}
            showIcon
            className="mt-4"
          />
        )}

        {!tieneFechasInscripcion && (
          <Alert
            message="Sin ventana definida"
            description="Las inscripciones se controlarán manualmente desde el estado del taller (abiertas/pausadas)."
            type="info"
            showIcon
            className="mt-4"
          />
        )}
      </Card>

      {/* Información sobre suscripciones */}
      <Card title="ℹ️ Resumen del comportamiento" size="small">
        <Alert
          message="¿Cómo funcionarán las suscripciones?"
          description={
            <div className="space-y-2">
              {tieneFechasActividades ? (
                <>
                  <p className="font-semibold">Con fechas de actividades configuradas:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Compra:</strong> El usuario paga y reserva su cupo inmediatamente</li>
                    <li><strong>Inicio de suscripción:</strong> {dayjs(watchFechasActividades[0]).format('DD/MM/YYYY')} (día de inicio de actividades)</li>
                    <li><strong>Duración:</strong> {esTallerCorto 
                      ? `${diasTaller} días exactos (taller corto)` 
                      : watchTipoDuracion ? `Según plan contratado y modalidad (${getDescripcionModalidad()})` : 'Según plan contratado'}
                    </li>
                    {!esTallerCorto && (
                      <li><strong>Límite:</strong> La suscripción se ajusta automáticamente si supera el {dayjs(watchFechasActividades[1]).format('DD/MM/YYYY')}</li>
                    )}
                  </ul>
                </>
              ) : (
                <>
                  <p className="font-semibold">Sin fechas de actividades (comportamiento estándar):</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Inicio:</strong> El día en que el usuario realiza el pago</li>
                    <li><strong>Duración:</strong> Según plan y modalidad {watchTipoDuracion ? `(${getDescripcionModalidad()})` : ''}</li>
                    <li><strong>Ejemplo:</strong> Compra el 15/marzo con plan mensual → termina el 15/abril</li>
                  </ul>
                </>
              )}

              {/* 🆕 Información de control de inscripciones */}
              {tieneFechasInscripcion && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="font-semibold text-yellow-800 mb-2">
                    🔒 Control automático de inscripciones:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2 text-yellow-900">
                    <li>Las inscripciones se habilitarán automáticamente el {dayjs(watchInscripcionInicio).format('DD/MM/YYYY [a las] HH:mm')} hrs</li>
                    <li>Se cerrarán automáticamente el {dayjs(watchInscripcionFin).format('DD/MM/YYYY [a las] HH:mm')} hrs</li>
                    <li>No necesitas activarlas manualmente si el taller está activo</li>
                  </ul>
                </div>
              )}
            </div>
          }
          type="info"
          showIcon
        />
      </Card>
    </div>
  );
}
